import { useState, useEffect, useRef, useCallback } from 'react'
import { aiApi } from '../api/client'

// AI Chat WebSocket hook with auth-aware connection management
// WebSocket connections must go directly to the backend since SWA linked backends don't proxy WebSockets
const BACKEND_HOST = 'project-ares-bbcec6hwdygddxca.westus2-01.azurewebsites.net'

const getWebSocketUrl = () => {
  // Check for explicit environment variable first
  if (import.meta.env.VITE_WS_URL) {
    return import.meta.env.VITE_WS_URL
  }
  
  // Local development - use same host
  const hostname = window.location.hostname
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/api/ai/ws/chat`
  }

  // Production (SWA custom domain or *.azurestaticapps.net) - connect
  // directly to the App Service backend. SWA cannot proxy WebSocket.
  return `wss://${BACKEND_HOST}/api/ai/ws/chat`
}

const WS_BASE_URL = getWebSocketUrl()

export function useAIChat() {
  const [connected, setConnected] = useState(false)
  const [conversationId, setConversationId] = useState(null)
  const [messages, setMessages] = useState([])
  const [isTyping, setIsTyping] = useState(false)
  const [toolExecutions, setToolExecutions] = useState([])
  const [statusMessage, setStatusMessage] = useState(null)
  const [error, setError] = useState(null)
  const [uploadingFiles, setUploadingFiles] = useState(false)
  
  const wsRef = useRef(null)
  const reconnectTimeoutRef = useRef(null)
  const reconnectAttemptsRef = useRef(0)
  const MAX_RECONNECT_ATTEMPTS = 5
  const authFailedRef = useRef(false)

  const connect = useCallback(async () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return
    
    // Don't reconnect if auth permanently failed or max retries exceeded
    if (authFailedRef.current) {
      console.log('AI Chat: Skipping reconnect - auth not available')
      return
    }
    if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
      console.log('AI Chat: Max reconnect attempts reached, stopping')
      setError('Unable to connect to AI chat. Please refresh the page.')
      return
    }

    try {
      // Get auth principal from SWA Easy Auth BEFORE opening WebSocket
      let authPrincipal = null
      console.log('AI Chat: Fetching auth info before WebSocket connect')
      
      try {
        const authResponse = await fetch('/.auth/me')
        const authData = await authResponse.json()
        
        if (authData.clientPrincipal) {
          authPrincipal = authData.clientPrincipal
          console.log('AI Chat: Got clientPrincipal for', authPrincipal.userDetails)
        } else {
          console.warn('AI Chat: No clientPrincipal found - user may not be logged in')
          authFailedRef.current = true
          return
        }
      } catch (authError) {
        console.warn('AI Chat: Could not fetch auth info:', authError)
        authFailedRef.current = true
        return
      }

      // Connect WebSocket WITHOUT auth in URL (App Service Easy Auth strips query params)
      // Auth is sent as the first message after connection opens
      console.log('AI Chat: Opening WebSocket connection to', WS_BASE_URL)
      wsRef.current = new WebSocket(WS_BASE_URL)

      wsRef.current.onopen = () => {
        console.log('AI Chat: WebSocket opened, sending auth message')
        // Send auth as first message - backend expects this before any other messages
        wsRef.current.send(JSON.stringify({
          type: 'auth',
          userId: authPrincipal.userId,
          userDetails: authPrincipal.userDetails,
          identityProvider: authPrincipal.identityProvider || 'aad'
        }))
      }

      wsRef.current.onmessage = (event) => {
        const data = JSON.parse(event.data)
        
        switch (data.type) {
          case 'auth_success':
            // Auth confirmed by backend - now fully connected
            setConnected(true)
            setError(null)
            reconnectAttemptsRef.current = 0
            console.log('AI Chat: Auth confirmed, fully connected')
            break

          case 'auth_failed':
            // Auth rejected - don't retry, user needs to re-login
            console.error('AI Chat: Auth rejected by backend:', data.content)
            authFailedRef.current = true
            setError('Authentication failed. Please refresh the page.')
            wsRef.current?.close(1000, 'Auth failed')
            break

          case 'conversation_created':
            setConversationId(data.conversation_id)
            break
            
          case 'conversation_loaded':
            setConversationId(data.conversation_id)
            setMessages(data.messages || [])
            break
            
          case 'typing':
            setIsTyping(data.status)
            if (!data.status) setStatusMessage(null)
            break
            
          case 'status':
            setStatusMessage(data.message)
            break
            
          case 'tool_execution':
            setToolExecutions(prev => {
              const existing = prev.find(t => t.tool === data.tool && t.status === 'running')
              if (existing && data.status === 'complete') {
                return prev.map(t => 
                  t.tool === data.tool && t.status === 'running' ? { ...data } : t
                )
              }
              return [...prev, data]
            })
            break
            
          case 'response':
            setMessages(prev => [...prev, {
              role: 'assistant',
              content: data.content,
              tool_calls: data.tool_calls,
              created_at: new Date().toISOString()
            }])
            setToolExecutions([])
            setStatusMessage(null)
            break
            
          case 'error':
            setError(data.content)
            setIsTyping(false)
            setStatusMessage(null)
            break
            
          case 'pong':
            // Heartbeat response
            break
        }
      }

      wsRef.current.onclose = (event) => {
        console.log('AI Chat: WebSocket closed', { code: event.code, reason: event.reason, wasClean: event.wasClean })
        setConnected(false)
        // Don't reconnect if auth failed, close was clean, or server rejected auth (4xxx codes)
        if (!authFailedRef.current && event.code !== 1000 && event.code < 4000) {
          reconnectAttemptsRef.current += 1
          const delay = Math.min(3000 * Math.pow(2, reconnectAttemptsRef.current - 1), 30000) // Exponential backoff, max 30s
          console.log(`AI Chat: Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})`)
          reconnectTimeoutRef.current = setTimeout(connect, delay)
        } else if (event.code >= 4000) {
          // Server explicitly rejected - mark auth as failed
          console.log('AI Chat: Server rejected connection (code', event.code, '), not retrying')
          authFailedRef.current = true
        }
      }

      wsRef.current.onerror = (err) => {
        console.error('AI Chat: WebSocket error', err)
        // Don't overwrite a more specific error
        setError(prev => prev || 'Connection error – unable to reach AI service')
      }
    } catch (err) {
      console.error('AI Chat: Failed to create WebSocket:', err)
      setError('Failed to connect')
    }
  }, [])

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'Panel closed')  // Clean close code
      wsRef.current = null
    }
    // Reset state so reopening the panel tries fresh
    reconnectAttemptsRef.current = 0
    authFailedRef.current = false
    setConnected(false)
  }, [])

  // Don't auto-connect - let the consumer call connect() when ready
  // This prevents WebSocket spam when the chat panel is hidden

  const newConversation = useCallback((projectId = null) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'new_conversation',
        project_id: projectId
      }))
      setMessages([])
      setToolExecutions([])
    }
  }, [])

  const loadConversation = useCallback((convId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'load_conversation',
        conversation_id: convId
      }))
    }
  }, [])

  const sendMessage = useCallback(async (content, projectId = null, files = []) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      let parsedFiles = []
      let fileContent = null
      
      // Parse files via backend if any
      if (files.length > 0) {
        setUploadingFiles(true)
        try {
          const parsePromises = files.map(file => aiApi.parseFile(file))
          const results = await Promise.all(parsePromises)
          parsedFiles = results.map(res => ({
            filename: res.data.filename,
            file_type: res.data.file_type,
            row_count: res.data.row_count,
            sheet_count: res.data.sheet_count,
          }))
          // Combine all parsed content
          fileContent = results.map(res => res.data.content).join('\n\n')
        } catch (err) {
          console.error('Failed to parse files:', err)
          const detail = err.response?.data?.detail || 'Failed to parse one or more files'
          setError(detail)
          setUploadingFiles(false)
          return
        }
        setUploadingFiles(false)
      }
      
      // Add user message to local state immediately (with file info)
      setMessages(prev => [...prev, {
        role: 'user',
        content,
        files: parsedFiles.length > 0 ? parsedFiles : undefined,
        created_at: new Date().toISOString()
      }])
      
      wsRef.current.send(JSON.stringify({
        type: 'message',
        content,
        project_id: projectId,
        file_content: fileContent,  // Parsed text goes to backend for Claude
      }))
      setError(null)
    }
  }, [])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  return {
    connected,
    conversationId,
    messages,
    isTyping,
    toolExecutions,
    statusMessage,
    error,
    uploadingFiles,
    newConversation,
    loadConversation,
    sendMessage,
    clearError,
    connect,
    disconnect,
  }
}
